/**
 * Service Area Store — source of truth for the currently selected service area.
 *
 * Persisted to sessionStorage so direct navigation / page refresh to nested
 * routes (e.g. Service Area Contact) preserves the active service-area id.
 * In embedded (AngularJS) mode the setters also dual-write to $rootScope.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { setRootScopeProperty, getRootScopeProperty } from '@/lib/legacyContext';
import { getMountConfig } from '@/mountConfig';

export type ServiceArea = Record<string, unknown> | null;

interface ServiceAreaState {
  updateServiceArea: ServiceArea;
  serviceAreaId: string | number | null;
}

interface ServiceAreaActions {
  setUpdateServiceArea: (v: ServiceArea) => void;
  setServiceAreaId: (v: string | number | null) => void;
  hydrateFromLegacy: () => void;
  reset: () => void;
}

type ServiceAreaStore = ServiceAreaState & ServiceAreaActions;

const INITIAL_STATE: ServiceAreaState = {
  updateServiceArea: null,
  serviceAreaId: null,
};

function syncToLegacy(key: string, value: unknown): void {
  try {
    if (getMountConfig().mode === 'embedded') {
      setRootScopeProperty(key, value);
    }
  } catch {
    // graceful
  }
}

export const useServiceAreaStore = create<ServiceAreaStore>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      setUpdateServiceArea: (v) => {
        set({ updateServiceArea: v });
        syncToLegacy('updateServiceArea', v);
      },
      setServiceAreaId: (v) => {
        set({ serviceAreaId: v });
        syncToLegacy('serviceAID', v);
      },

      hydrateFromLegacy: () => {
        // Embedded-only — see propertyStore.hydrateFromLegacy for full reasoning.
        if (getMountConfig().mode !== 'embedded') return;
        set({
          updateServiceArea: getRootScopeProperty<ServiceArea>('updateServiceArea') ?? null,
          serviceAreaId: getRootScopeProperty<string | number | null>('serviceAID') ?? null,
        });
      },

      reset: () => set({ ...INITIAL_STATE }),
    }),
    {
      name: 'spiglass-service-area',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        updateServiceArea: state.updateServiceArea,
        serviceAreaId: state.serviceAreaId,
      }),
    }
  )
);

export const useUpdateServiceArea = (): ServiceArea =>
  useServiceAreaStore((s) => s.updateServiceArea);
export const useServiceAreaId = (): string | number | null =>
  useServiceAreaStore((s) => s.serviceAreaId);

/** Test-only. */
export function __resetServiceAreaStoreForTests(): void {
  useServiceAreaStore.setState({ ...INITIAL_STATE });
  try {
    sessionStorage.removeItem('spiglass-service-area');
  } catch {
    // graceful
  }
}
